import { LocalParticipant } from "livekit-client";
import { useCallback, useEffect, useMemo } from "react";
import { useLocalVideo } from "./useLocalVideo";
import { useLocalAudio } from "./useLocalAudio";
import { useTrackPublication } from "@/hooks/livekit/useTrackPublication";

interface ToggleMediaOpts {
  localParticipant?: LocalParticipant;
}

export function useToggleMediaTracks({ localParticipant }: ToggleMediaOpts) {
  const video = useLocalVideo();
  const audio = useLocalAudio({ enabled: false });
  const setPublished = useTrackPublication(localParticipant);
  const toggleCamera = useCallback(async () => {
    if (video.track) {
      await setPublished(video.track, false);
      video.stop();
    } else {
      const t = await video.start();
      await setPublished(t, true);
    }
  }, [setPublished, video]);
  const toggleMicrophone = useCallback(async () => {
    if (audio.track) {
      await setPublished(audio.track, false);
      audio.stop();
    } else {
      const t = await audio.start();
      await setPublished(t, true);
    }
  }, [audio, setPublished]);

  useEffect(() => {
    if (localParticipant && video.track) setPublished(video.track, true);
  }, [localParticipant, video.track, setPublished]);
  useEffect(() => {
    if (localParticipant && audio.track) setPublished(audio.track, true);
  }, [localParticipant, audio.track, setPublished]);

  const cameraEnabled = useMemo(() => !!video.track, [video.track]);
  const micEnabled = useMemo(() => !!audio.track, [audio.track]);

  return useMemo(
    () =>
      ({
        toggleCamera,
        toggleMicrophone,
        cameraEnabled,
        micEnabled,
        localVideoTrack: video.track,
        localAudioTrack: audio.track,
      }) as const,
    [
      audio.track,
      cameraEnabled,
      micEnabled,
      toggleCamera,
      toggleMicrophone,
      video.track,
    ]
  );
}
